import React, { useMemo } from 'react';

import { formatMoney, formatRate } from '~support-at-home/lib/budgetFormat';
import { toCents } from '~support-at-home/lib/overLimits';

import { Button } from '~components';
import SectionCard from '~components/SectionCard';
import { TreeTable, TreeTableColumn, TreeTableTreeColumn, useTreeExpansion } from '~components/tree-table';
import SegmentedBar from '~components/visualisation/segmented-bar/SegmentedBar';
import SegmentedBarLegend from '~components/visualisation/segmented-bar/SegmentedBarLegend';
import SectionInfo from '~support-at-home/components/common/SectionInfo';

import { TRACKER_TEXT } from './trackerText';
import { BreakdownRow, TrackerBreakdownView } from './transforms';

interface ComponentProps {
  data: TrackerBreakdownView;
}

type MoneyKey = 'allocated' | 'booked' | 'claimed';

const copy = TRACKER_TEXT.breakdown;

const treeColumn: TreeTableTreeColumn<BreakdownRow> = {
  key: 'name',
  header: copy.columns.name,
  width: '22%',
  render: (row) => (
    <>
      {row.name}
      {row.subLabel && <span className="tracker-breakdown-sub-label">{row.subLabel}</span>}
      {row.code && <span className="tracker-breakdown-code">{`· ${row.code}`}</span>}
    </>
  ),
};

const moneyColumn = (key: MoneyKey): TreeTableColumn<BreakdownRow> => ({
  key,
  header: copy.columns[key],
  caption: { id: `breakdown-${key}`, content: copy[key] },
  align: 'end',
  render: (row) => formatMoney(row[key]),
});

// Over its allocation: a negative Remaining, which is also Utilisation above 100% (TAV-3482)
const isOver = (row: BreakdownRow): boolean => toCents(row.remaining) < 0;

const toggleLabel = (row: BreakdownRow, expanded: boolean): string =>
  expanded ? copy.toggle.hideServices(row.name) : copy.toggle.showServices(row.name);

const BudgetBreakdown: React.FC<ComponentProps> = ({ data }) => {
  const { rows, scaleMax, legend } = data;
  const expansion = useTreeExpansion(rows);

  const columns = useMemo<TreeTableColumn<BreakdownRow>[]>(
    () => [
      {
        key: 'bar',
        header: copy.columns.bar,
        caption: { id: 'breakdown-bar', content: copy.bar },
        width: '24%',
        // Hover percentages are shares of the row's Allocated, as in Budget Overview
        render: (row) => (
          <SegmentedBar
            segments={row.segments}
            scaleMax={scaleMax}
            percentBase={row.allocated}
            ariaLabel={row.barLabel}
          />
        ),
      },
      moneyColumn('allocated'),
      moneyColumn('booked'),
      moneyColumn('claimed'),
      {
        key: 'remaining',
        header: copy.columns.remaining,
        caption: { id: 'breakdown-remaining', content: copy.remaining },
        align: 'end',
        render: (row) =>
          isOver(row) ? (
            <span className="tracker-breakdown-over">{formatMoney(row.remaining)}</span>
          ) : (
            formatMoney(row.remaining)
          ),
      },
      {
        key: 'utilisation',
        header: copy.columns.utilisation,
        caption: { id: 'breakdown-utilisation', content: copy.utilisation },
        align: 'end',
        render: (row) =>
          isOver(row) ? (
            <span className="tracker-breakdown-over">{formatRate(row.utilisation)}</span>
          ) : (
            formatRate(row.utilisation)
          ),
      },
    ],
    [scaleMax],
  );

  return (
    <SectionCard
      title={copy.title}
      description={copy.description}
      className="tracker-breakdown"
      dataCy="tracker-breakdown"
      titleAddon={<SectionInfo id="breakdown-section" label={copy.title} content={copy.section} />}
      headerRight={
        <Button
          color="info"
          outline
          size="sm"
          onClick={expansion.allExpanded ? expansion.collapseAll : expansion.expandAll}
          dataCy="tracker-breakdown-toggle-all-button"
        >
          {expansion.allExpanded ? copy.collapseAll : copy.expandAll}
        </Button>
      }
    >
      <SegmentedBarLegend items={legend} dataCy="tracker-breakdown-legend" />
      <TreeTable
        rows={rows}
        treeColumn={treeColumn}
        columns={columns}
        isExpanded={expansion.isExpanded}
        onToggle={expansion.toggle}
        toggleLabel={toggleLabel}
        caption={copy.tableCaption}
        minWidth={1100}
        dataCy="tracker-breakdown-table"
      />
    </SectionCard>
  );
};

export default BudgetBreakdown;
